import { useState } from "react";
import { updateWhatsApp } from "../../../../services/api/channels/utils/change_whatsapp_config";

const ALLOWED_TYPES = [
  "Geral",
  "Avisos",
  "Faturas",
  "Suporte",
  "Marketing",
  "Outros",
];

const SYSTEM_LINK_BUTTON_TYPES = ["opt_out_url"];
const LINK_BUTTON_TYPES = ["open_url", "opt_out_url"];
const MAX_AUTO_REPLY_MESSAGES = 2;

const createAutoReplyMessage = (index = 0, overrides = {}) => ({
  id: `auto-reply-${index + 1}`,
  type: "text",
  content: "",
  fromMe: true,
  buttons: [],
  ...overrides,
});

export function useWhatsAppConfig() {
  const [autoReplyEnabled, setAutoReplyEnabled] = useState(false);
  const [autoReplyError, setAutoReplyError] = useState(false);
  const [minTime, setMinTime] = useState(null);
  const [maxTime, setMaxTime] = useState(null);

  const [messages, setMessages] = useState([
    {
      id: "client",
      type: "text",
      content: "Olá, preciso de suporte.",
      fromMe: false,
    },
    createAutoReplyMessage(),
  ]);

  // ==========================
  // VALIDADORES
  // ==========================
  const areButtonsValid = (buttons = []) => {
    if (!Array.isArray(buttons)) return false;
    if (!buttons.length) return true;
    if (buttons.length > 1) return false;
    if (buttons.filter((btn) => LINK_BUTTON_TYPES.includes(btn.type)).length > 1) {
      return false;
    }

    return buttons.every((btn) => {
      if (!btn.type) return false;
      if (!btn.label?.trim()) return false;
      if (btn.type === "open_url" && !btn.url?.trim()) return false;
      if (SYSTEM_LINK_BUTTON_TYPES.includes(btn.type)) return true;
      return true;
    });
  };

  const areMessagesValid = () =>
    messages
      .filter((m) => m.fromMe)
      .every((m) => String(m.content || "").trim() && areButtonsValid(m.buttons));

  // ==========================
  // PAYLOAD CANÔNICO (FRONT)
  // ==========================
  const buildPayload = () => ({
    messages: messages
      .filter((m) => m.fromMe)
      .filter(
        (m) => autoReplyEnabled && String(m.content || "").trim(),
      )
      .slice(0, MAX_AUTO_REPLY_MESSAGES)
      .map((m) => ({
        type: m.type,
        content: m.content,
        ...(m.buttons?.length && {
          buttons: m.buttons.map((b) => ({
            type: b.type,
            label: b.label,
            ...(b.type === "open_url" && { url: b.url }),
          })),
        }),
      })),
  });

  const save = async (channelId, channelType, type = "Geral") => {
    setAutoReplyError(false);

    if (!channelId || !channelType) {
      console.error("ChannelId ou channelType não informado");
      return false;
    }

    const normalizedType = ALLOWED_TYPES.includes(type) ? type : "Geral";

    if (autoReplyEnabled) {
      const autoReplyMessages = messages.filter((m) => m.fromMe);
      if (
        !autoReplyMessages.length ||
        autoReplyMessages.some((m) => !String(m.content || "").trim()) ||
        autoReplyMessages.length > MAX_AUTO_REPLY_MESSAGES
      ) {
        setAutoReplyError(true);
        return false;
      }
    }

    if (channelType === "whatsapp_api" && !areMessagesValid()) {
      alert("Existem botões não configurados corretamente.");
      return false;
    }

    // 🔹 Payload base
    const payload = {
      channel: channelType,
      channel_id: channelId,
      category: normalizedType,
      auto_reply: {
        enabled: autoReplyEnabled,
      },
    };

    // 🔹 API → mensagens + botões
    if (channelType === "whatsapp_api") {
      payload.auto_reply.messages = buildPayload().messages;
    }

    // 🔹 Web → somente texto
    if (channelType === "whatsapp_web") {
      const autoReply = messages.find((m) => m.fromMe);
      payload.auto_reply.message = autoReply?.content || "";
    }

    try {
      await updateWhatsApp(payload);
      return true;
    } catch (err) {
      console.error("Erro ao salvar configuração do WhatsApp:", err);
      alert("Erro ao salvar a configuração.");
      return false;
    }
  };

  return {
    messages,
    setMessages,
    createAutoReplyMessage,
    maxAutoReplyMessages: MAX_AUTO_REPLY_MESSAGES,
    autoReplyEnabled,
    setAutoReplyEnabled,
    autoReplyError,
    minTime,
    maxTime,
    setMinTime,
    setMaxTime,
    save,
    buildPayload,
  };
}
